// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows: SRP — load one-time share token and render details.
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { publicApiClient, API_ENDPOINTS } from '../config/api';
import VoiceDetailModal from './VoiceDetailModal';

/** Full viewport white shell (overrides global :root dark background on public share view). */
const shareViewShellSx = {
  minHeight: '100vh',
  width: '100%',
  bgcolor: '#ffffff',
  color: 'text.primary',
  boxSizing: 'border-box',
};

/**
 * React 18 Strict Mode (dev) runs effects twice; a one-time share GET would fire twice
 * and the second request always gets 404 after the first consumes the token.
 * Dedupe: only one in-flight fetch per token value for this mount cycle.
 */
const ShareVoiceTranscriptionPage = () => {
  const { token } = useParams();
  const [bundle, setBundle] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const fetchStartedForTokenRef = useRef(null);

  useEffect(() => {
    if (!token) {
      setNotFound(true);
      return;
    }

    if (fetchStartedForTokenRef.current === token) {
      return;
    }
    fetchStartedForTokenRef.current = token;

    const url = `${API_ENDPOINTS.VOICE_PUBLIC_SHARE}/${encodeURIComponent(token)}`;
    publicApiClient
      .get(url)
      .then((res) => {
        if (res.data?.success) {
          setBundle(res.data);
        } else {
          setNotFound(true);
        }
      })
      .catch((err) => {
        if (err.response?.status === 404) {
          setNotFound(true);
        } else {
          setLoadError(err.response?.data?.detail || err.message || 'Failed to load');
        }
      });
  }, [token]);

  if (notFound) {
    return <Navigate to="/404" replace />;
  }

  if (loadError) {
    return (
      <Box sx={{ ...shareViewShellSx, p: 4, textAlign: 'center' }}>
        <Typography color="error">{loadError}</Typography>
      </Box>
    );
  }

  if (!bundle) {
    return (
      <Box
        sx={{
          ...shareViewShellSx,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2 }}>
          Loading…
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={shareViewShellSx}>
      <VoiceDetailModal
        displayMode="fullPage"
        readOnly
        open
        preloadedBundle={bundle}
        fileId={bundle.task_id}
        fileName={bundle.transcription?.file_name || bundle.analysis?.file_name || bundle.task_id}
        onClose={() => {}}
      />
    </Box>
  );
};

export default ShareVoiceTranscriptionPage;
